import SecondarySidebar from 'components/SecondarySidebar/SecondarySidebar'
import { usePlaces } from 'providers/PlacesProvider'
import { useTranslation } from 'react-i18next'
import { Room } from '@mui/icons-material'
import { Box, ToggleButtonGroup, Typography } from '@mui/material'
import { SidebarSectionHeader } from 'components/SecondarySidebar/elements'
import { useNavigate } from 'react-router-dom'
import {
  useRoutePlanning,
  ToggleDeliveriesToDisplay,
} from 'providers/RoutePlanningProvider'
import { MouseEvent } from 'react'
import theme from 'lib/theme'
import { ROUTES } from 'routes/routes'
import { ORDER_STATUS } from 'types/api/order'
import { StatusRoundIcon, ToggleRoutesButton } from './elements'

const Sidebar = () => {
  const { selectedPlace } = usePlaces()
  const {
    deliveries,
    setToggleDeliveriesToDisplay,
    toggleDeliveriesToDisplay,
  } = useRoutePlanning()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const prevListingPage = localStorage.getItem('routesListingPage')

  const toggleControl = {
    value: toggleDeliveriesToDisplay,
    exclusive: true,
    fullWidth: true,
    orientation: 'vertical' as const,
    sx: { display: 'block' },
    onChange: (
      event: MouseEvent<HTMLElement>,
      newAlignment: ToggleDeliveriesToDisplay
    ) => {
      if (newAlignment !== null) setToggleDeliveriesToDisplay(newAlignment)
    },
  }

  return (
    <SecondarySidebar
      scrollable
      header={t('routes')}
      onGoBackClick={() => navigate(`/route${prevListingPage}`)}>
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <Box display='flex' alignItems='center'>
          <Room fontSize='small' viewBox='4 0 25 25' />
          <Typography variant='body2'>
            {selectedPlace ? selectedPlace.name : t('selectStore')}
          </Typography>
        </Box>
        <button type='button' onClick={() => navigate(ROUTES.ROUTE)}>
          <Typography variant='button'>{t('change')} </Typography>
        </button>
      </Box>
      <Box mt={4} width='100%'>
        <SidebarSectionHeader>{t('orders')}</SidebarSectionHeader>
        <ToggleButtonGroup {...toggleControl}>
          <ToggleRoutesButton
            fullWidth
            value='all'
            disabled={toggleDeliveriesToDisplay === 'all'}>
            <StatusRoundIcon color='transparent' />
            {t('routePlanning.all')}
          </ToggleRoutesButton>
          <ToggleRoutesButton
            fullWidth
            value='unrouted'
            disabled={toggleDeliveriesToDisplay === 'unrouted'}>
            <StatusRoundIcon color={theme.palette.text.primary} />
            {t('routePlanning.unrouted')}
          </ToggleRoutesButton>
        </ToggleButtonGroup>
      </Box>
      <Box mt={4}>
        <SidebarSectionHeader>
          {t('routePlanning.routesInPlanning')}
        </SidebarSectionHeader>
        <ToggleButtonGroup {...toggleControl}>
          {deliveries
            ?.filter(
              (delivery) => delivery.orderStatus === ORDER_STATUS.COLLECTED
            )
            .map((delivery) => (
              <ToggleRoutesButton
                data-cy='route in planning'
                key={delivery.id}
                fullWidth
                value={delivery.id}
                disabled={toggleDeliveriesToDisplay === delivery.id}>
                <Box display='flex'>
                  <StatusRoundIcon color={delivery.color} />
                  <Typography variant='body2'>
                    {t('routePlanning.routeName')} {delivery.routeName}
                  </Typography>
                </Box>
              </ToggleRoutesButton>
            ))}
        </ToggleButtonGroup>
      </Box>
      <Box mt={4}>
        <SidebarSectionHeader>
          {t('routePlanning.routesInDelivery')}
        </SidebarSectionHeader>
        <ToggleButtonGroup {...toggleControl}>
          {deliveries
            ?.filter(
              (delivery) => delivery.orderStatus === ORDER_STATUS.DELIVERING
            )
            .map((delivery) => (
              <ToggleRoutesButton
                data-cy='route in delivery'
                key={delivery.id}
                fullWidth
                value={delivery.id}
                disabled={toggleDeliveriesToDisplay === delivery.id}>
                <Box display='flex'>
                  <StatusRoundIcon color={delivery.color} />
                  <Typography variant='body2'>
                    {t('routePlanning.routeName')} {delivery.routeName}
                  </Typography>
                </Box>
              </ToggleRoutesButton>
            ))}
        </ToggleButtonGroup>
      </Box>
      <Box mt={4}>
        <SidebarSectionHeader>
          {t('routePlanning.routesDelivered')}
        </SidebarSectionHeader>
        <ToggleButtonGroup {...toggleControl}>
          {deliveries
            ?.filter(
              (delivery) => delivery.orderStatus === ORDER_STATUS.DELIVERED
            )
            .map((delivery) => (
              <ToggleRoutesButton
                key={delivery.id}
                fullWidth
                value={delivery.id}
                disabled={toggleDeliveriesToDisplay === delivery.id}>
                <Box display='flex'>
                  <StatusRoundIcon color={delivery.color} />
                  <Typography variant='body2'>
                    {t('routePlanning.routeName')} {delivery.routeName}
                  </Typography>
                </Box>
              </ToggleRoutesButton>
            ))}
        </ToggleButtonGroup>
      </Box>
    </SecondarySidebar>
  )
}

export default Sidebar
